import { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@stockline/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@stockline/ui/components/dialog";
import { MobilePDFPreview } from "@/components/pdf-viewer/MobilePDFPreview";
import { downloadMedia, fetchMediaBlob } from "./useChatMediaUrl";

/**
 * WhatsApp-style PDF attachment: a clean tappable tile (icon + name, no
 * size/type chrome) that opens a full-screen in-app PDF viewer with a single
 * download action in the header.
 */
export function ChatMediaPdf({
  url,
  name,
}: {
  readonly url: string | null;
  readonly name: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [failed, setFailed] = useState(false);

  // The pdf.js preview needs the bytes — fetch them only once the viewer
  // opens. `fetchMediaBlob` resolves null on failure (network error, expired
  // URL), which must land in an error state — otherwise the dialog spins
  // forever. `failed` is also the retry trigger: resetting it re-runs this
  // effect.
  useEffect(() => {
    if (!open || !url || blob || failed) return;
    let cancelled = false;
    void fetchMediaBlob(url).then((b) => {
      if (cancelled) return;
      if (b) setBlob(b);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [open, url, blob, failed]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!url}
        data-testid="chat-media-pdf"
        className="flex w-full items-center gap-3 rounded-2xl border bg-card p-3 text-left transition-colors hover:bg-accent disabled:opacity-60"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-red-500/10 text-red-600 dark:text-red-400">
          {url ? (
            <FileText className="size-5" />
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {name}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[100dvh] max-h-none w-screen max-w-none flex-col gap-0 border-none p-0 sm:rounded-none">
          <div className="flex shrink-0 items-center gap-2 border-b bg-card px-4 py-2.5 pr-14">
            <FileText className="size-4 shrink-0 text-red-600 dark:text-red-400" />
            <DialogTitle className="min-w-0 flex-1 truncate text-sm font-medium">
              {name}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => url && void downloadMedia(url, name)}
              aria-label={t("chat.download", "Télécharger")}
              className="size-8"
            >
              <Download className="size-4" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto bg-muted/40">
            {blob ? (
              <MobilePDFPreview pdfBlob={blob} className="h-full" />
            ) : failed ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t("chat.failedToLoadPdf", "Échec du chargement du PDF")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFailed(false)}
                >
                  {t("common.retry", "Réessayer")}
                </Button>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
